import { Navigate, useLocation, Outlet } from 'react-router-dom';
import { useProfile } from '@/lib/hooks/use-profile';
import { useIsAdmin } from '@/lib/hooks/use-is-admin';

/**
 * Guard component that enforces role-based route access:
 * - Clients can only access /client/* routes
 * - Genius/Freelancers can only access /genius/* routes
 * - Admins can access all routes
 * 
 * Redirects users to their appropriate dashboard if they try to access routes
 * that don't match their role.
 */
export function RoleBasedGuard() {
  const location = useLocation();
  const { profile, isLoading } = useProfile();
  const isAdmin = useIsAdmin();

  // Show loading spinner while profile is loading
  if (isLoading || !profile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[hsl(var(--color-4))]" />
      </div>
    );
  }

  // Admins can access all routes
  if (isAdmin) {
    return <Outlet />;
  }

  const pathname = location.pathname;
  const isClientRoute = pathname.startsWith('/client');
  const isGeniusRoute = pathname.startsWith('/genius');
  const isAdminRoute = pathname.startsWith('/admin');
  const isDashboardRoute = pathname === '/dashboard';

  // Allow dashboard redirect route for all authenticated users
  if (isDashboardRoute) {
    return <Outlet />;
  }

  // Block admin routes for non-admins
  if (isAdminRoute) {
    // Redirect to appropriate dashboard based on role
    if (profile.is_client) {
      return <Navigate to="/client/dashboard" replace />;
    } else if (profile.is_genius) {
      return <Navigate to="/genius/dashboard" replace />;
    } else {
      return <Navigate to="/" replace />;
    }
  }

  // Client users can only access client routes
  if (profile.is_client && !profile.is_genius) {
    if (!isClientRoute) {
      return <Navigate to="/client/dashboard" replace />;
    }
    return <Outlet />;
  }

  // Genius/Freelancer users can only access genius routes
  if (profile.is_genius && !profile.is_client) {
    if (!isGeniusRoute) {
      return <Navigate to="/genius/dashboard" replace />;
    }
    return <Outlet />;
  }

  // Users with both roles can access both route sets
  if (profile.is_client && profile.is_genius) {
    if (isClientRoute || isGeniusRoute) {
      return <Outlet />;
    }
    // Default to client dashboard if accessing neither
    return <Navigate to="/client/dashboard" replace />;
  }

  // Users with no role should go to onboarding (handled by OnboardingGuard)
  return <Outlet />;
}

